/**
 * The diff of everything that changed in the selected range, as one document.
 *
 * The whole range is rendered at once rather than a section at a time: reading
 * a day's work means scrolling through it, not clicking twenty rows. The change
 * map beside it is a navigator — picking a row scrolls here.
 *
 * Three ways to read the same rows, because judging writing and auditing a
 * change are different jobs: `inline` marks the words that moved, `split` puts
 * the two versions beside each other, and `final` shows only the new text with
 * a bar in the margin — the mode you actually read in.
 *
 * Long stretches of untouched prose collapse. Without that, a two-thousand-word
 * chapter with one edited sentence is a page of scrolling to find it.
 */

import { Lock } from '@mui/icons-material'
import { Box, CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import type { SectionDiffResponse } from '@shared/contentHistory'
import type { BlockDiffRow, DiffRun } from '@shared/diff/types'
import React from 'react'

export type DiffViewMode = 'inline' | 'split' | 'final'

interface SectionDiffViewProps {
  sections: SectionDiffResponse[]
  loading: boolean
  /** True when the range held more sections than the server would send. */
  truncated?: boolean
  totalSections?: number
  direction: 'ltr' | 'rtl'
  fontFamily?: string
  mode: DiffViewMode
  onModeChange: (mode: DiffViewMode) => void
  /** Section key to scroll to — set when a change-map row is picked. */
  focusKey?: string | null
}

/** Unchanged runs shorter than this are shown rather than collapsed. */
const COLLAPSE_THRESHOLD = 3

/**
 * DOM id for a section's heading, so the change map can scroll to it.
 *
 * Only whitespace is replaced. Stripping every non-ASCII character would map
 * every Hebrew filename to the same run of underscores, and two files can carry
 * the same block id — ids are unique per file, not per book.
 */
function sectionAnchorId(key: string): string {
  return `history-section-${key.replace(/\s+/g, '_')}`
}

function runsToText(runs: DiffRun[], keep: DiffRun['op'][]): string {
  return runs
    .filter((run) => keep.includes(run.op))
    .map((run) => run.text)
    .join('')
}

function isHeading(row: BlockDiffRow): boolean {
  return row.blockType === 'heading'
}

function InlineRuns({ runs, only }: { runs: DiffRun[]; only?: DiffRun['op'][] }) {
  return (
    <>
      {runs
        .filter((run) => !only || only.includes(run.op))
        .map((run, index) => {
          if (run.op === 'same') {
            return <React.Fragment key={index}>{run.text}</React.Fragment>
          }
          const inserted = run.op === 'insert'
          return (
            <Box
              key={index}
              component="span"
              sx={{
                bgcolor: inserted ? 'var(--diff-insert-bg)' : 'var(--diff-delete-bg)',
                color: inserted ? 'inherit' : 'var(--diff-delete-text)',
                textDecoration: inserted ? 'none' : 'line-through',
                textDecorationThickness: '1px',
                borderRadius: '2px',
                boxShadow: `inset 0 -1px 0 ${inserted ? 'var(--diff-insert-border)' : 'var(--diff-delete-border)'}`,
              }}
            >
              {run.text}
            </Box>
          )
        })}
    </>
  )
}

function BlockShell({
  row,
  direction,
  fontFamily,
  children,
}: {
  row: BlockDiffRow
  direction: 'ltr' | 'rtl'
  fontFamily?: string
  children: React.ReactNode
}) {
  const changed = row.kind !== 'unchanged'
  const barColor =
    row.kind === 'added'
      ? 'var(--diff-insert-border)'
      : row.kind === 'removed'
        ? 'var(--diff-delete-border)'
        : row.kind === 'moved'
          ? 'var(--diff-moved-border)'
          : 'var(--diff-changed-bar)'

  return (
    <Box
      dir={direction}
      sx={{
        position: 'relative',
        px: 1.5,
        py: 0.75,
        // The change bar hangs on the reading-start edge, as a margin mark would.
        [direction === 'rtl' ? 'borderRight' : 'borderLeft']: '3px solid',
        borderColor: changed ? barColor : 'transparent',
        ...(row.hasEncrypted
          ? { bgcolor: 'var(--encrypt-marked-bg)', outline: '1px solid var(--encrypt-marked-border)' }
          : {}),
      }}
    >
      {row.hasEncrypted && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
          <Lock sx={{ fontSize: 12, color: 'var(--encrypt-marked-badge)' }} />
          <Typography sx={{ fontSize: '0.6rem', color: 'var(--encrypt-marked-badge)', fontWeight: 700 }}>
            Encrypted
          </Typography>
        </Box>
      )}
      {row.kind === 'moved' && (
        <Typography sx={{ fontSize: '0.62rem', color: 'var(--diff-moved-border)', fontWeight: 700, mb: 0.25 }}>
          Moved
        </Typography>
      )}
      <Typography
        component="div"
        sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily,
          fontSize: isHeading(row) ? '1rem' : '0.92rem',
          fontWeight: isHeading(row) ? 700 : 400,
          lineHeight: 1.7,
          color: 'var(--text-primary)',
        }}
      >
        {children}
      </Typography>
    </Box>
  )
}

function CollapsedRun({ count, onExpand }: { count: number; onExpand: () => void }) {
  return (
    <Box
      onClick={onExpand}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        cursor: 'pointer',
        color: 'var(--text-tertiary)',
        '&:hover': { color: 'var(--text-secondary)' },
      }}
    >
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'var(--border-subtle)' }} />
      <Typography sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
        {count} unchanged block{count === 1 ? '' : 's'} — show
      </Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: 'var(--border-subtle)' }} />
    </Box>
  )
}

function SectionBody({
  diff,
  mode,
  direction,
  fontFamily,
}: {
  diff: SectionDiffResponse
  mode: DiffViewMode
  direction: 'ltr' | 'rtl'
  fontFamily?: string
}) {
  const [expanded, setExpanded] = React.useState<Set<number>>(new Set())

  React.useEffect(() => {
    setExpanded(new Set())
  }, [diff.key, mode])

  // Group consecutive untouched blocks so a single edit is not buried in them.
  const groups: Array<
    { kind: 'rows'; rows: BlockDiffRow[] } | { kind: 'collapsed'; rows: BlockDiffRow[]; id: number }
  > = []
  let run: BlockDiffRow[] = []
  const flushRun = () => {
    if (run.length === 0) {
      return
    }
    groups.push(
      run.length >= COLLAPSE_THRESHOLD
        ? { kind: 'collapsed', rows: run, id: groups.length }
        : { kind: 'rows', rows: run },
    )
    run = []
  }

  for (const row of diff.rows) {
    // In "final" mode a removed block has nothing left to show.
    if (mode === 'final' && row.kind === 'removed') {
      continue
    }
    if (row.kind === 'unchanged') {
      run.push(row)
      continue
    }
    flushRun()
    groups.push({ kind: 'rows', rows: [row] })
  }
  flushRun()

  const renderRow = (row: BlockDiffRow, index: number) => {
    if (mode === 'split' && row.kind === 'edited') {
      return (
        <Box
          key={`${row.blockId}-${index}`}
          sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, alignItems: 'start' }}
        >
          <BlockShell row={{ ...row, kind: 'removed' }} direction={direction} fontFamily={fontFamily}>
            <InlineRuns runs={row.runs} only={['same', 'delete']} />
          </BlockShell>
          <BlockShell row={{ ...row, kind: 'added' }} direction={direction} fontFamily={fontFamily}>
            <InlineRuns runs={row.runs} only={['same', 'insert']} />
          </BlockShell>
        </Box>
      )
    }

    if (mode === 'final') {
      const text = runsToText(row.runs, ['same', 'insert'])
      if (text.length === 0) {
        return null
      }
      return (
        <BlockShell key={`${row.blockId}-${index}`} row={row} direction={direction} fontFamily={fontFamily}>
          {text}
        </BlockShell>
      )
    }

    return (
      <BlockShell key={`${row.blockId}-${index}`} row={row} direction={direction} fontFamily={fontFamily}>
        <InlineRuns runs={row.runs} />
      </BlockShell>
    )
  }

  return (
    <>
      {diff.structural.length > 0 && (
        <Box sx={{ px: 1.5, pb: 0.5 }}>
          {diff.structural.map((change, index) => (
            <Typography key={index} sx={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
              {change.label}: {change.before ?? '—'} → {change.after ?? '—'}
            </Typography>
          ))}
        </Box>
      )}

      {groups.map((group, groupIndex) =>
        group.kind === 'collapsed' && !expanded.has(group.id) ? (
          <CollapsedRun
            key={`collapsed-${group.id}`}
            count={group.rows.length}
            onExpand={() => setExpanded((current) => new Set(current).add(group.id))}
          />
        ) : (
          <React.Fragment key={`group-${groupIndex}`}>{group.rows.map(renderRow)}</React.Fragment>
        ),
      )}
    </>
  )
}

export default function SectionDiffView({
  sections,
  loading,
  truncated,
  totalSections,
  direction,
  fontFamily,
  mode,
  onModeChange,
  focusKey,
}: SectionDiffViewProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null)

  // Picking a row in the change map scrolls this pane rather than replacing it.
  React.useEffect(() => {
    if (!focusKey) {
      return
    }
    const target = scrollRef.current?.querySelector(`#${CSS.escape(sectionAnchorId(focusKey))}`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [focusKey, sections])

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1,
          borderBottom: '1px solid var(--border-subtle)',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ flex: 1, fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
          {loading
            ? 'Reading…'
            : sections.length === 0
              ? 'No prose changed in this range'
              : `${sections.length} section${sections.length === 1 ? '' : 's'}${
                  truncated ? ` of ${totalSections}` : ''
                }`}
        </Typography>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_event, next) => next && onModeChange(next)}
          sx={{ '& .MuiToggleButton-root': { fontSize: '0.68rem', py: 0.25, px: 1, textTransform: 'none' } }}
        >
          <ToggleButton value="inline">Inline</ToggleButton>
          <ToggleButton value="split">Side by side</ToggleButton>
          <ToggleButton value="final">Final only</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* No top padding: a sticky header offsets from the scrollport's padding
          edge, so any padding here becomes a band above the stuck header that
          the previous section's text scrolls through. */}
      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', pt: 0, pb: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {!loading && sections.length === 0 && (
          <Typography sx={{ p: 3, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
            Nothing changed in this range.
          </Typography>
        )}

        {!loading &&
          sections.map((diff) => (
            <Box key={diff.key} sx={{ pb: 2 }}>
              <Box
                id={sectionAnchorId(diff.key)}
                dir={direction}
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  px: 1.5,
                  py: 0.75,
                  bgcolor: 'var(--bg-surface-alt)',
                  borderBottom: '1px solid var(--border-subtle)',
                  // Scroll target sits under the sticky header without hiding the title.
                  scrollMarginTop: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: diff.isChapterSection ? 700 : 500,
                    color: diff.isChapterSection ? 'var(--text-primary)' : 'var(--text-secondary)',
                  }}
                >
                  {diff.isChapterSection && (
                    <Box component="span" sx={{ color: 'var(--text-tertiary)' }}>
                      ^{' '}
                    </Box>
                  )}
                  {diff.displayTitle}
                </Typography>
                <Typography sx={{ fontSize: '0.66rem', color: 'var(--text-tertiary)' }}>
                  {diff.ancestorLabels.length > 0 ? `${diff.ancestorLabels.join(' › ')} · ` : ''}
                  {diff.filename} · {/* Isolated: the signs are bidi-neutral and mirror inside Hebrew. */}
                  <Box component="span" dir="ltr" sx={{ unicodeBidi: 'isolate' }}>
                    <Box component="span" sx={{ color: 'var(--diff-insert-border)' }}>
                      +{diff.wordsAdded}
                    </Box>{' '}
                    <Box component="span" sx={{ color: 'var(--diff-delete-border)' }}>
                      −{diff.wordsRemoved}
                    </Box>
                  </Box>
                </Typography>
              </Box>

              <SectionBody diff={diff} mode={mode} direction={direction} fontFamily={fontFamily} />
            </Box>
          ))}

        {truncated && !loading && (
          <Typography sx={{ p: 2, fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
            Showing the first {sections.length} of {totalSections} changed sections. Narrow the range to see the rest.
          </Typography>
        )}
      </Box>
    </Box>
  )
}
